import { useCallback, useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useToast } from '../hooks/useToast.js';
import { ApiError } from '../api/client.js';
import { deleteRecipe, listRecipes } from '../api/recipes.js';
import { readRecipeCache, writeRecipeCache } from '../api/recipeCache.js';
import type { Recipe } from '../api/types.js';
import { RecipeCatalog } from './recipes/RecipeCatalog.js';
import { RecipeDetail } from './recipes/RecipeDetail.js';
import { RecipeForm } from './recipes/RecipeForm.js';
import { ScraperDialog } from './recipes/ScraperDialog.js';
import { FridgeSheet } from './recipes/FridgeSheet.js';
import { IngredientDialog } from './recipes/IngredientDialog.js';

type Mode = 'catalog' | 'detail' | 'form';

export interface RecipesProps {
  initialDetailId?: number | null;
  onConsumed?: () => void;
}

export function Recipes({
  initialDetailId = null,
  onConsumed,
}: RecipesProps = {}): ReactElement {
  const toast = useToast();
  const [mode, setMode] = useState<Mode>(initialDetailId !== null ? 'detail' : 'catalog');
  const [refreshKey, setRefreshKey] = useState(0);
  const [detailId, setDetailId] = useState<number | null>(initialDetailId);
  const [editing, setEditing] = useState<Recipe | null>(null);
  const [recipes, setRecipes] = useState<Recipe[]>(() => readRecipeCache() ?? []);
  const [fridgeIds, setFridgeIds] = useState<number[]>([]);
  const [fridgeOpen, setFridgeOpen] = useState(false);
  const [scraperOpen, setScraperOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingIngredientName, setEditingIngredientName] = useState<string | null>(null);

  const refresh = useCallback((): void => {
    setRefreshKey((k) => k + 1);
  }, []);

  // Always preload the recipe list so we can find the selected one for detail view.
  useEffect(() => {
    let cancelled = false;
    listRecipes({}).then((rows) => {
      if (cancelled) return;
      setRecipes(rows);
      writeRecipeCache(rows);
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  // Honor a (possibly late-arriving) initialDetailId by switching to detail mode.
  // Clear it on the parent only after the new mode has flushed, so the same
  // Recipes instance stays mounted (no remount-as-catalog).
  useEffect(() => {
    if (initialDetailId === null) return;
    setDetailId(initialDetailId);
    setMode('detail');
    queueMicrotask(() => onConsumed?.());
  }, [initialDetailId, onConsumed]);

  const detail = detailId === null ? null : recipes.find((r) => r.id === detailId) ?? null;

  function openNew(): void {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(r: Recipe): void {
    setEditing(r);
    setFormOpen(true);
  }

  function openDetail(id: number): void {
    setDetailId(id);
    setMode('detail');
    onConsumed?.();
  }

  function backToCatalog(): void {
    setMode('catalog');
    setDetailId(null);
  }

  async function onDelete(r: Recipe): Promise<void> {
    try {
      await deleteRecipe(r.id);
      toast.push('success', 'Recipe deleted');
      backToCatalog();
      refresh();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.push(
          'error',
          'Recipe is used in a meal plan and cannot be deleted.',
        );
      } else {
        const msg = err instanceof Error ? err.message : 'Delete failed';
        toast.push('error', msg);
      }
    }
  }

  return (
    <div className="flex h-full flex-col" data-testid="recipes-view">
      {mode === 'catalog' ? (
        <RecipeCatalog
          fridgeIds={fridgeIds}
          onRemoveFridge={(id) => setFridgeIds(fridgeIds.filter((x) => x !== id))}
          onClearFridge={() => setFridgeIds([])}
          onOpenFridge={() => setFridgeOpen(true)}
          onOpenNew={openNew}
          onOpenScraper={() => setScraperOpen(true)}
          onOpenRecipe={openDetail}
          refreshKey={refreshKey}
        />
      ) : null}

      {mode === 'detail' && detail ? (
        <div className="flex flex-col">
          <button
            type="button"
            onClick={backToCatalog}
            className="self-start rounded-md px-3 py-2 text-sm"
            style={{ color: 'var(--accent)' }}
            data-testid="recipe-back"
          >
            ← Back to recipes
          </button>
          <RecipeDetail
            recipe={detail}
            onEdit={() => openEdit(detail)}
            onDelete={() => void onDelete(detail)}
          />
        </div>
      ) : null}

      <RecipeForm
        open={formOpen}
        editing={editing}
        onClose={() => setFormOpen(false)}
        onSaved={(r) => {
          refresh();
          setDetailId(r.id);
          setMode('detail');
        }}
      />

      <ScraperDialog
        open={scraperOpen}
        onClose={() => setScraperOpen(false)}
        onSaved={refresh}
      />

      <FridgeSheet
        open={fridgeOpen}
        selected={fridgeIds}
        onClose={() => setFridgeOpen(false)}
        onApply={(ids) => {
          setFridgeIds(ids);
          setMode('catalog');
        }}
      />

      <IngredientDialog
        open={editingIngredientName !== null}
        initialName={editingIngredientName ?? ''}
        onClose={() => setEditingIngredientName(null)}
        onSaved={() => {
          refresh();
          setEditingIngredientName(null);
        }}
      />
    </div>
  );
}